/**
 * `ApiClient` — singleton wrapper that adds auth + 401-refresh + JSON
 * parsing to `fetch`. Every consumer that hits an authenticated edge
 * service imports one of the exported singletons below (`usersClient`,
 * `dataClient`, …) — this is the ONE master file where they all live;
 * never construct a `new ApiClient(...)` in feature code.
 *
 * Per-call code at consumers is one line:
 *
 *   const result = await usersClient.get<T>('/v2/...')
 *   const result = await usersClient.post<T>('/v2/...', body)
 *   await usersClient.delete('/v2/...')
 *
 * Non-JSON needs: `stream(path, body?)` returns the raw `Response` (SSE /
 * blobs), `beacon(path, body?)` fires a keepalive write for tab-close drains.
 *
 * No manual `JSON.stringify`, no `method:` boilerplate, no token handling, no
 * refresh logic. 409 throws `ConflictError`; every other non-2xx throws
 * `ApiError` (which carries the status + path).
 *
 * Refresh-on-401 is shared session-wide via `authFetchWithRefresh` —
 * concurrent 401s across ALL APIs coalesce to a single Cognito
 * `refreshSession()` call. The refresh issues new access AND id tokens
 * together, so callers on either token type benefit from one refresh.
 */

import { API, COMPANION_API, LLM_API, RETROSTOCK_API, STAGE } from '@/config/constants'
import { authFetchWithRefresh } from './auth-fetch'
import { ApiError, ConflictError } from './errors'

type TokenType = 'access' | 'id' | 'none'

/**
 * Per-request options. Pass-through to `fetch`:
 *
 *   - `signal`: cancel via `AbortController` (React Query / unmount cleanup).
 *   - `timeoutMs`: convenience — composed with `signal` so either aborts
 *     the request. Native `AbortSignal.timeout(ms)` under the hood.
 *   - `headers`: merged onto the default `Content-Type: application/json`.
 */
export interface ApiClientOptions {
  signal?: AbortSignal
  timeoutMs?: number
  headers?: HeadersInit
}

/** {@link ApiClient.stream} options — `ApiClientOptions` plus a method override
 *  (defaults to POST, the shape of the SSE run endpoints). */
export interface ApiStreamOptions extends ApiClientOptions {
  method?: string
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly tokenType: TokenType,
    private readonly credentials?: RequestCredentials,
  ) {}

  /** GET. Throws on non-2xx. Returns parsed JSON, or `null` on 204. */
  get<T>(path: string, options?: ApiClientOptions): Promise<T> {
    return this.request<T>(path, 'GET', undefined, options)
  }

  /** POST with optional JSON body. Throws `ConflictError` on 409. */
  post<T>(path: string, body?: unknown, options?: ApiClientOptions): Promise<T> {
    return this.request<T>(path, 'POST', body, options)
  }

  /** POST that returns unwrapped data plus HTTP status and optional meta. */
  postEnvelope<T>(
    path: string,
    body?: unknown,
    options?: ApiClientOptions,
  ): Promise<EnvelopeResult<T>> {
    return this.requestEnvelope<T>(path, 'POST', body, options)
  }

  /** PUT with optional JSON body. Throws `ConflictError` on 409. */
  put<T>(path: string, body?: unknown, options?: ApiClientOptions): Promise<T> {
    return this.request<T>(path, 'PUT', body, options)
  }

  /** DELETE. Throws on non-2xx. */
  async delete(path: string, options?: ApiClientOptions): Promise<void> {
    await this.request<void>(path, 'DELETE', undefined, options)
  }

  /**
   * GET that returns the full envelope: `{ data, status, meta }`. Use when
   * the caller needs the HTTP status (telemetry) or the `meta` block — the
   * plain `get()` discards both. `data` is still envelope-unwrapped.
   */
  getEnvelope<T>(path: string, options?: ApiClientOptions): Promise<EnvelopeResult<T>> {
    return this.requestEnvelope<T>(path, 'GET', undefined, options)
  }

  /**
   * Streaming request (SSE / chunked). Returns the raw `Response` with auth +
   * 401-refresh applied; the caller reads `res.body` (typically via
   * `lib/parse-sse-stream.ts`). Defaults to POST — the shape of every SSE "run"
   * endpoint — override with `options.method` for a GET stream; pass
   * `Accept: text/event-stream` via `options.headers` if the endpoint needs it.
   * Throws `ApiError` on a non-2xx (the stream never opened); the happy path
   * returns the open `Response` for the caller to drain.
   */
  async stream(path: string, body?: unknown, options?: ApiStreamOptions): Promise<Response> {
    const res = await authFetchWithRefresh(`${this.baseUrl}${path}`, {
      method: options?.method ?? 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      tokenType: this.tokenType,
      credentials: this.credentials,
      signal: composeSignals(options?.signal, options?.timeoutMs),
      headers: options?.headers,
    })
    if (!res.ok) throw await apiErrorFrom(res, path)
    return res
  }

  /**
   * Fire-and-forget keepalive write for tab-close drains (`keepalive: true` lets
   * the request outlive the unload). POST by default; pass `options.method` for
   * a PATCH/PUT drain. Never throws — an unload-time drain must not break
   * navigation — and does not meaningfully await a refresh (the page is going
   * away). Resolves once the request is dispatched.
   */
  async beacon(path: string, body?: unknown, options?: { method?: string }): Promise<void> {
    try {
      await authFetchWithRefresh(`${this.baseUrl}${path}`, {
        method: options?.method ?? 'POST',
        body: body !== undefined ? JSON.stringify(body) : undefined,
        tokenType: this.tokenType,
        credentials: this.credentials,
        keepalive: true,
      })
    } catch {
      // best-effort: a keepalive drain must never surface an error
    }
  }

  // ─── Internal ───

  /** Shared request path. Returns data + HTTP status + (optional) meta. */
  private async requestEnvelope<T>(
    path: string,
    method: string,
    body: unknown,
    options: ApiClientOptions | undefined,
  ): Promise<EnvelopeResult<T>> {
    const res = await authFetchWithRefresh(`${this.baseUrl}${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      tokenType: this.tokenType,
      credentials: this.credentials,
      signal: composeSignals(options?.signal, options?.timeoutMs),
      headers: options?.headers,
    })

    if (res.status === 409) throw new ConflictError()
    if (!res.ok) throw await apiErrorFrom(res, path)
    if (res.status === 204) return { data: null as T, status: 204 }
    const raw = await res.json()
    return { ...splitEnvelope<T>(raw), status: res.status }
  }

  /** Thin wrapper over {@link requestEnvelope} that returns just `data`. */
  private async request<T>(
    path: string,
    method: string,
    body: unknown,
    options: ApiClientOptions | undefined,
  ): Promise<T> {
    return (await this.requestEnvelope<T>(path, method, body, options)).data
  }
}

/**
 * Build an `ApiError` for a non-2xx response, attaching its body so callers can
 * read the error detail — parsed JSON when it is JSON (e.g. a backtest's
 * `{detail:{node_id,reason}}`), otherwise the raw text (some stream endpoints
 * send a plain-text reason), or `undefined` when empty. Consuming the body here
 * is safe: the response is discarded on the error path anyway. Falls back to
 * `json()` for response-likes without `text()` (test stubs).
 */
async function apiErrorFrom(res: Response, path: string): Promise<ApiError> {
  let body: unknown
  try {
    if (typeof res.text === 'function') {
      const raw = await res.text()
      if (raw) {
        try {
          body = JSON.parse(raw)
        } catch {
          body = raw
        }
      }
    } else if (typeof res.json === 'function') {
      body = await res.json()
    }
  } catch {
    body = undefined
  }
  return new ApiError(res.status, path, body)
}

/** Result of {@link ApiClient.getEnvelope}. */
export interface EnvelopeResult<T> {
  data: T
  status: number
  meta?: Record<string, unknown>
}

/**
 * Split the `{success, data, meta}` envelope applied by the edge services'
 * ResponseEnvelopeMiddleware into `{ data, meta }`. Plain payloads (no
 * envelope) pass through as `{ data: raw }`. Error envelopes never reach
 * here — non-2xx already threw above.
 */
function splitEnvelope<T>(raw: unknown): { data: T; meta?: Record<string, unknown> } {
  if (
    raw !== null &&
    typeof raw === 'object' &&
    'success' in raw &&
    'data' in raw &&
    'meta' in raw
  ) {
    const r = raw as { data: T; meta?: Record<string, unknown> }
    return { data: r.data, meta: r.meta }
  }
  return { data: raw as T }
}

/**
 * Combine a caller-provided signal with a timeout signal. Either firing
 * aborts the request. Returns undefined when neither is supplied.
 *
 * Uses `AbortSignal.any` (Node 20+, all modern browsers). If the runtime
 * predates it, the timeout still works but the caller's signal is
 * preferred — that's a graceful fallback that hasn't broken anyone we
 * support.
 */
function composeSignals(
  signal: AbortSignal | undefined,
  timeoutMs: number | undefined,
): AbortSignal | undefined {
  if (!signal && timeoutMs === undefined) return undefined
  if (!signal) return AbortSignal.timeout(timeoutMs!)
  if (timeoutMs === undefined) return signal
  // Modern path: combine both signals.
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)])
  }
  // Fallback for older runtimes: caller's signal wins.
  return signal
}

// ─── Singletons ───
//
// One per (base URL, token type). Tests can replace by stubbing the
// individual export via `vi.mock('@/services/api/api-client', ...)` or by
// mocking the consumer module above this layer.

export const usersClient = new ApiClient(API.users, 'access')
export const dataClient = new ApiClient(API.data, 'access')
/** Public proxy endpoints (no auth header). */
export const proxyClient = new ApiClient(API.proxy, 'none')
/** Python edge-llm-api — only the TTS clip route still lives there. */
export const llmClient = new ApiClient(LLM_API, 'access')
/**
 * Retrostock (Nautilus) synchronous single-run backtest API. **Id token,
 * required**: its verifier (RetroStockV3 lib/nautilus/api/auth.py) accepts
 * either token type, but `/run` reads `custom:edge_user_id`, which only the id
 * token carries. Same reason as provision-migration.
 */
export const singleRunClient = new ApiClient(
  RETROSTOCK_API,
  'id',
  // Local dev proxies to staging. Cookie content can trigger staging WAF;
  // this API authenticates with the explicit ID-token header instead.
  import.meta.env.DEV || STAGE === 'staging' || STAGE === 'beta' ? 'omit' : undefined,
)
/**
 * edge-companion-api, the TS AI plane: analyst, deep research, knowledge/learn,
 * summaries, tools/skills, strategy AI, layout generation, insights, and the
 * companion thread sync under `/v1/companion/threads`.
 */
export const companionClient = new ApiClient(COMPANION_API, 'access')
